import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, inject } from '@angular/core';
import { BreadCrumbElement } from '../../models/bread-crumb-element';
import { SelectionArguments } from '../../models/selection-arguments';
import { SelectionType } from '../../enums/selection-type';
import { StructureType } from '../../enums/structure-type';
import { SplitViewObserverService } from '../split-view/split-view-observer.service';
import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'ldw-structure-bread-crumb',
    templateUrl: './structure-bread-crumb.component.html',
    styleUrls: ['./structure-bread-crumb.component.scss'],
    standalone: false
})
export class StructureBreadCrumbComponent implements OnDestroy {
  private splitViewObserver = inject(SplitViewObserverService);

  _path: string | undefined = undefined;
  elements: BreadCrumbElement[] = [];
  scrollOffset = 0;
  _scrollingEnabled = false;
  sink = new Subscription();

  constructor() {
    const splitViewObserver = this.splitViewObserver;

    this.sink.add(
      splitViewObserver
        .observe()
        .pipe(tap(() => this.isScrollingNeeded()))
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
  }

  @Output() itemSelected: EventEmitter<SelectionArguments> = new EventEmitter();

  @Input() set path(value: string | undefined) {
    if (this._path == undefined && value == undefined) {
      return;
    }
    if (this._path == value) {
      return;
    }

    this._path = value;
    if (value == undefined) {
      this.elements = [];
      return;
    }
    this.parseElements();
  }

  @ViewChild('breadCrumbContainer') breadCrumbcontainer: ElementRef<HTMLDivElement> | undefined;

  private parseElements() {
    this.elements = [];
    const parts = this._path?.split('/');
    if (parts == undefined) {
      return;
    }
    parts.forEach((part) => {
      const subs = part.split(';');
      if (subs != undefined && subs.length == 2) {
        const bci = new BreadCrumbElement();
        bci.link = subs[0];
        bci.name = subs[1];
        this.elements.push(bci);
      }
    });
    setTimeout(() => {
      this.scrollOffset = 0;
      this.isScrollingNeeded();
    });
  }

  navTo(target: string) {
    const args = new SelectionArguments();
    args.id = target;
    args.structureType = StructureType.Folder;
    args.action = SelectionType.Selected;
    this.itemSelected.emit(args);
  }

  setScrollOffset(offsetDelta: number) {
    if (!this._scrollingEnabled || !this.breadCrumbcontainer) {
      this.scrollOffset = 0;
      return;
    }
    const breadcrumbContainerElement = this.breadCrumbcontainer.nativeElement;
    if (breadcrumbContainerElement.scrollWidth === breadcrumbContainerElement.clientWidth && !this.scrollOffset) {
      return;
    } else if (breadcrumbContainerElement.scrollWidth + offsetDelta <= breadcrumbContainerElement.clientWidth) {
      this.scrollOffset =
        -(breadcrumbContainerElement.scrollWidth - breadcrumbContainerElement.clientWidth) + this.scrollOffset;
    } else if (this.scrollOffset + offsetDelta > 0) {
      this.scrollOffset = 0;
    } else {
      this.scrollOffset += offsetDelta;
    }
    this.isScrollingNeeded();
  }

  private isScrollingNeeded() {
    if (!this.breadCrumbcontainer) {
      this._scrollingEnabled = false;
      return;
    }
    const breadcrumbContainerElement = this.breadCrumbcontainer.nativeElement;
    if (this.scrollOffset !== 0 || breadcrumbContainerElement.scrollWidth > breadcrumbContainerElement.clientWidth) {
      this._scrollingEnabled = true;
      return;
    }
    this._scrollingEnabled = false;
    return;
  }
}
